<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useMarkdownLoader } from '@composables/useMarkdownLoader'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  url: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['close'])

const { t } = useI18n()
const { content, isLoading, error, loadMarkdown } = useMarkdownLoader()

// Focus trap management
const modalRef = ref(null)
const firstFocusableElement = ref(null)
const lastFocusableElement = ref(null)

// Load markdown when modal opens
watch(() => props.isOpen, (isOpen) => {
  if (isOpen && props.url) {
    loadMarkdown(props.url)

    // Prevent body scroll
    document.body.style.overflow = 'hidden'

    // Setup focus trap after DOM updates
    setTimeout(() => {
      setupFocusTrap()
      focusModal()
    }, 100)
  } else {
    // Restore body scroll
    document.body.style.overflow = ''
  }
})

// Setup focus trap
const setupFocusTrap = () => {
  if (!modalRef.value) return

  const focusableElements = modalRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  if (focusableElements.length > 0) {
    firstFocusableElement.value = focusableElements[0]
    lastFocusableElement.value = focusableElements[focusableElements.length - 1]
  }
}

// Focus modal on open
const focusModal = () => {
  if (firstFocusableElement.value) {
    firstFocusableElement.value.focus()
  }
}

// Handle keyboard navigation
const handleKeydown = (e) => {
  if (!props.isOpen) return

  // Escape key closes modal
  if (e.key === 'Escape') {
    close()
    return
  }

  // Tab key for focus trap
  if (e.key === 'Tab') {
    if (e.shiftKey) {
      // Shift+Tab: moving backwards
      if (document.activeElement === firstFocusableElement.value) {
        e.preventDefault()
        lastFocusableElement.value?.focus()
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === lastFocusableElement.value) {
        e.preventDefault()
        firstFocusableElement.value?.focus()
      }
    }
  }
}

// Close modal
const close = () => {
  emit('close')
}

// Handle backdrop click
const handleBackdropClick = (e) => {
  if (e.target === e.currentTarget) {
    close()
  }
}

// Add keyboard listener
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

// Remove keyboard listener and restore scroll
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="'modal-title-' + title"
        @click="handleBackdropClick"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"></div>

        <!-- Modal Container -->
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <Transition
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-300"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="isOpen"
              ref="modalRef"
              class="relative bg-theme-primary rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col border border-theme-default"
            >
              <!-- Header -->
              <div class="flex items-center justify-between p-6 border-b border-theme-default">
                <h2
                  :id="'modal-title-' + title"
                  class="text-xl font-semibold text-theme-primary"
                >
                  {{ title }}
                </h2>
                <button
                  @click="close"
                  type="button"
                  class="text-theme-tertiary transition-colors p-1 rounded-lg cursor-pointer"
                  :aria-label="t('leadForm.modal.close')"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Content -->
              <div class="flex-1 overflow-y-auto p-6">
                <!-- Loading State -->
                <div
                  v-if="isLoading"
                  class="flex flex-col items-center justify-center py-12"
                  role="status"
                  aria-live="polite"
                >
                  <div class="animate-spin rounded-full h-12 w-12 border-b-4 loading-spinner mb-4"></div>
                  <p class="text-theme-secondary">{{ t('leadForm.modal.loading') }}</p>
                </div>

                <!-- Error State -->
                <div
                  v-else-if="error"
                  class="text-center py-12"
                  role="alert"
                >
                  <svg class="w-12 h-12 text-error mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-error font-medium mb-2">
                    {{ t('leadForm.modal.error') }}
                  </p>
                  <p class="text-theme-secondary text-sm">{{ error }}</p>
                </div>

                <!-- Content -->
                <div
                  v-else
                  class="markdown-content prose prose-sm sm:prose-base dark:prose-invert max-w-none"
                  v-html="content"
                ></div>
              </div>

              <!-- Footer -->
              <div class="p-6 border-t flex justify-end" style="border-color: var(--theme-border-default)">
                <button
                  @click="close"
                  type="button"
                  class="px-6 py-3 font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg theme-button-primary"
                >
                  {{ t('leadForm.modal.close') }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Ensure scrollable content area */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgb(168, 85, 247) rgb(243, 244, 246);
}

.dark .overflow-y-auto {
  scrollbar-color: rgb(168, 85, 247) rgb(55, 65, 81);
}

/* Webkit browsers */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgb(243, 244, 246);
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: rgb(55, 65, 81);
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgb(168, 85, 247);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgb(147, 51, 234);
}

/* Markdown content styling for dark mode */
.markdown-content :deep(*) {
  color: rgb(55, 65, 81); /* gray-700 for light mode */
}

.dark .markdown-content :deep(*) {
  color: rgb(209, 213, 219); /* gray-300 for dark mode */
}

/* Headings in dark mode */
.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
  color: rgb(17, 24, 39); /* gray-900 */
  font-weight: 600;
}

.dark .markdown-content :deep(h1),
.dark .markdown-content :deep(h2),
.dark .markdown-content :deep(h3),
.dark .markdown-content :deep(h4),
.dark .markdown-content :deep(h5),
.dark .markdown-content :deep(h6) {
  color: rgb(243, 244, 246); /* gray-100 */
}

/* Paragraphs and text */
.markdown-content :deep(p) {
  color: rgb(55, 65, 81); /* gray-700 */
}

.dark .markdown-content :deep(p) {
  color: rgb(209, 213, 219); /* gray-300 */
}

/* Lists */
.markdown-content :deep(ul),
.markdown-content :deep(ol),
.markdown-content :deep(li) {
  color: rgb(55, 65, 81); /* gray-700 */
}

.dark .markdown-content :deep(ul),
.dark .markdown-content :deep(ol),
.dark .markdown-content :deep(li) {
  color: rgb(209, 213, 219); /* gray-300 */
}

/* Strong/Bold text */
.markdown-content :deep(strong),
.markdown-content :deep(b) {
  color: rgb(17, 24, 39); /* gray-900 */
  font-weight: 600;
}

.dark .markdown-content :deep(strong),
.dark .markdown-content :deep(b) {
  color: rgb(243, 244, 246); /* gray-100 */
}

/* Emphasis/Italic */
.markdown-content :deep(em),
.markdown-content :deep(i) {
  color: rgb(55, 65, 81); /* gray-700 */
}

.dark .markdown-content :deep(em),
.dark .markdown-content :deep(i) {
  color: rgb(209, 213, 219); /* gray-300 */
}

/* Links */
.markdown-content :deep(a) {
  color: var(--theme-primary-color);
  text-decoration: none;
}

.markdown-content :deep(a:hover) {
  text-decoration: underline;
}

/* Code blocks */
.markdown-content :deep(code),
.markdown-content :deep(pre) {
  color: rgb(55, 65, 81); /* gray-700 */
  background-color: rgb(243, 244, 246); /* gray-100 */
}

.dark .markdown-content :deep(code),
.dark .markdown-content :deep(pre) {
  color: rgb(209, 213, 219); /* gray-300 */
  background-color: rgb(31, 41, 55); /* gray-800 */
}

/* Blockquotes */
.markdown-content :deep(blockquote) {
  color: rgb(75, 85, 99); /* gray-600 */
  border-left-color: rgb(209, 213, 219); /* gray-300 */
}

.dark .markdown-content :deep(blockquote) {
  color: rgb(156, 163, 175); /* gray-400 */
  border-left-color: rgb(75, 85, 99); /* gray-600 */
}

/* Tables */
.markdown-content :deep(table),
.markdown-content :deep(th),
.markdown-content :deep(td) {
  color: rgb(55, 65, 81); /* gray-700 */
  border-color: rgb(209, 213, 219); /* gray-300 */
}

.dark .markdown-content :deep(table),
.dark .markdown-content :deep(th),
.dark .markdown-content :deep(td) {
  color: rgb(209, 213, 219); /* gray-300 */
  border-color: rgb(75, 85, 99); /* gray-600 */
}

.dark .markdown-content :deep(th) {
  color: rgb(243, 244, 246); /* gray-100 */
  background-color: rgb(31, 41, 55); /* gray-800 */
}

/* Horizontal rule */
.markdown-content :deep(hr) {
  border-color: rgb(209, 213, 219); /* gray-300 */
}

.dark .markdown-content :deep(hr) {
  border-color: rgb(75, 85, 99); /* gray-600 */
}

/* Theme Button Primary */
.theme-button-primary {
  background-color: var(--theme-button-primary-bg);
  color: var(--theme-button-primary-text);
  border: 2px solid var(--theme-button-primary-border);
}

.theme-button-primary:hover {
  background-color: var(--theme-button-primary-bg-hover);
}

.theme-button-primary:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--theme-button-primary-border);
  opacity: 0.5;
}

.text-error {
  color: var(--theme-form-error-text);
}

.loading-spinner{
  border-color: var(--theme-button-primary-border);
}
.bg-theme-primary {
  background-color: var(--color-theme-bg-primary);
}
.text-theme-primary{
  color: var(--color-theme-primary-text);
}
.text-theme-secondary{
  color: var(--color-theme-secondary-text);
}
.text-theme-tertiary{
  color: var(--color-theme-text-tertiary);
}
.border-theme-default{
  border-color: var(--color-theme-border);
}
</style>
